import React, { useContext, useEffect, useState } from 'react';
import SubmitButton from '../../components/SubmitButton';
import {
  RouteComponentProps,
  useParams,
  withRouter,
  StaticContext,
} from 'react-router';
import {
  Order,
  OrderBoxesResponse,
  OrderResponse,
  OrderAssignmentResponse,
  PartnerUser,
  Partner,
  Warehouse,
} from '../../models/APIModels';
import Moment from 'moment';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import FileDownload from 'js-file-download';
import Barcode from '../../components/Barcode';
import StatusBlock from '../../components/StatusBlock';
import PaymentBlock from '../../components/PaymentBlock';
import PaymentStatusBlock from '../../components/PaymentStatusBlock';
import { Role } from '../../utilities/Role';
import UserContext from '../../contexts/UserContext';
import { NoteBlock } from '../../components/NoteBlock';
import { OrderBoxesDisplay } from '../User/Order';
import DPDShippingModal from '../../components/DPDShippingModal';
import {
  AdminPageShell,
  DataTable,
  DataTableColumn,
} from '../../components/admin/Shared';

interface OrderParams {
  id: string;
}

interface OrderComponentState {
  order?: Order;
}

function AdminOrderComponent({
  history,
}: RouteComponentProps<
  undefined,
  StaticContext,
  OrderComponentState
>): JSX.Element {
  const { id } = useParams<OrderParams>();
  const [order, setOrder] = useState(history.location?.state?.order);
  const [showDPDModal, setShowONSModal] = useState(false);
  const [assignedCollectionCourier, setAssignedCollectionCourier] = useState<PartnerUser | undefined>();
  const [assignedDeliveryCourier, setAssignedDeliveryCourier] = useState<PartnerUser | undefined>();
  const [assignedPartner, setAssignedPartner] = useState<Partner | undefined>();
  const [assignedWarehouse, setAssignedWarehouse] = useState<Warehouse | undefined>();
  const statuses = order?.statuses || [];
  const customer = order?.customer;
  const payments = order?.payments || [];
  const notes = order?.notes || [];
  const orderBoxes = order?.orderBoxes || [];
  const { user: loggedInUser } = useContext(UserContext);

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);

    if (!order) {
      // Try to fetch as regular order first
      axiosKK.get<OrderResponse>(`/order/${id}`)
        .then(({ data }) => {
          if (!cancelled) setOrder(data.order);
        })
        .catch(() => {
          // If that fails, try to fetch as removal order
          axiosKK.get<OrderResponse>(`/admin/removals/orders/${id}`)
            .then(({ data }) => {
              if (!cancelled) setOrder(data.order);
            })
            .catch((error) => {
              console.error('Error fetching order:', error);
            });
        });
    }
    return () => { cancelled = true; };
  }, [order, id]);

  useEffect(() => {
    let cancelled = false;
    if (order && order.storageRequired) {
      axiosKK
        .get<OrderAssignmentResponse>(
          `/admin/logistics/orders/${order.id}/assignment`
        )
        .then(({ data }) => {
          if (cancelled) return;
          // Use split couriers if present, otherwise fall back to legacy single courier
          setAssignedCollectionCourier(data.collectionCourier ?? data.courier);
          setAssignedDeliveryCourier(data.deliveryCourier ?? data.courier);
          setAssignedPartner(data.courierPartner);
          setAssignedWarehouse(data.warehouse);
        })
        .catch(() => {
          // Assignment info not available - ignore
        });
    }
    return () => { cancelled = true; };
  }, [order]);

  const orderTitle = `${
    order?.orderType === 'removal'
      ? 'Removal'
      : order?.suitcaseOrder
        ? 'Suitcase'
        : order?.storageRequired
          ? 'Storage'
          : 'Shipping'
  } Order Overview`;

  const barcodeColumns: DataTableColumn<OrderBoxesResponse>[] = [
    {
      key: 'image',
      label: 'Image',
      width: '72px',
      render: (row) => (
        <img
          src={row.box?.imageUrl}
          alt=""
          className="admin-data-table__image"
        />
      ),
    },
    {
      key: 'name',
      label: 'Item Name',
      render: (row) => row.box?.name || '—',
    },
    {
      key: 'barcode',
      label: 'Barcode',
      render: (row) => <Barcode>{row.barcode}</Barcode>,
    },
  ];

  return (
    <AdminPageShell
      variant="detail"
      title={orderTitle}
      subtitle={
        order
          ? `${order.orderType === 'removal' ? 'Removal order' : 'Storage order'} #${order.id} — created ${Moment(order.createdAt).format('D MMM YYYY')}.`
          : undefined
      }
      onBack={() => history.push('/admin/dashboard')}
    >
      <section className="print-doc-and-items-block">
        <div className="print-doc admin-print-doc">
          <div className="top-border">Basic Info</div>
          <div className="second-border">
            <p className="roomPack">
              {order?.roomPackIncluded ? 'Fulfilled' : 'Not fulfilled'}
            </p>
            <p className="date">
              Order Created At:{' '}
              {Moment(order?.createdAt).format('dddd, MMM Do YYYY')}
            </p>
          </div>
          <div className="basic-block">
            <div className="id-city-requirement">
              <div className="id-and-city">
                <p className="id">{order?.id}</p>
                <p className="city">{order?.orderType === 'removal' ? order?.collectionCity : order?.city}</p>
                {customer && (
                  <div
                    onClick={() =>
                      history.push(`/admin/customer/${customer.id}`)
                    }
                    className="name"
                  >
                    <p>
                      {customer?.firstName} {customer?.lastName}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="addresses-block">
              <div className="each">
                <Label>{order?.orderType === 'removal' ? 'Move Date & Collection Address:' : 'Collection Address:'}</Label>
                <p>{order?.collectionAddress}</p>
                <p>
                  {order?.orderType === 'removal'
                    ? order?.moveDate && Moment(order.moveDate).format('dddd, MMM Do YYYY')
                    : order?.collectionDate && Moment(order.collectionDate).format('dddd, MMM Do YYYY')}
                </p>
              </div>
              <div className="each">
                <Label>Delivery Address:</Label>
                <p>{order?.deliveryAddress}</p>
                <p>
                  {order?.deliveryDate &&
                    Moment(order.deliveryDate).format('dddd, MMM Do YYYY')}
                </p>
              </div>
            </div>
            {order?.storageRequired && (
              <div className="addresses-block">
                <div className="each">
                  <Label>Collection Courier:</Label>
                  <p>
                    {assignedCollectionCourier
                      ? [assignedCollectionCourier.firstName, assignedCollectionCourier.lastName].filter(Boolean).join(' ') || assignedCollectionCourier.email || `Courier #${assignedCollectionCourier.id}`
                      : 'Not assigned'}
                  </p>
                  {assignedPartner?.companyName && (
                    <p>{assignedPartner.companyName}</p>
                  )}
                </div>
                <div className="each">
                  <Label>Delivery Courier:</Label>
                  <p>
                    {assignedDeliveryCourier
                      ? [assignedDeliveryCourier.firstName, assignedDeliveryCourier.lastName].filter(Boolean).join(' ') || assignedDeliveryCourier.email || `Courier #${assignedDeliveryCourier.id}`
                      : 'Not assigned'}
                  </p>
                </div>
                <div className="each">
                  <Label>Assigned Warehouse:</Label>
                  <p>
                    {assignedWarehouse
                      ? (assignedWarehouse.name || `Warehouse #${assignedWarehouse.id}`) + (assignedWarehouse.city ? ` - ${assignedWarehouse.city}` : '')
                      : 'Not assigned'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {order?.orderType === 'removal' ? (
            <div className="customer-print-doc">
              <div className="items-block">
                <div className="eachBox">
                  <div className="border-top" />
                  <div className="content">
                    <div className="basic">
                      <h3>Removal Service</h3>
                      <p><strong>Tier:</strong> {order?.tierName}</p>
                    </div>
                    <div className="cost">
                      <Label htmlFor={`removal-details-${order?.id}`}>Property Details:</Label>
                      <div className="costDetails">
                        <Label htmlFor={`removal-floor-${order?.id}`}>Property Floor:</Label>
                        <p id={`removal-floor-${order?.id}`}>
                          {order?.propertyFloor === '0' ? 'Ground Floor' : `Floor ${order?.propertyFloor}`}
                        </p>
                        {order?.propertyHasLift !== null && (
                          <>
                            <Label htmlFor={`removal-lift-${order?.id}`}>Lift Available:</Label>
                            <p id={`removal-lift-${order?.id}`}>{order?.propertyHasLift ? 'Yes' : 'No'}</p>
                          </>
                        )}
                        <Label htmlFor={`removal-furniture-${order?.id}`}>Furniture:</Label>
                        <p id={`removal-furniture-${order?.id}`}>{order?.propertyHasFurniture ? 'Yes' : 'No'}</p>
                        <Label htmlFor={`removal-parking-${order?.id}`}>Parking Available:</Label>
                        <p id={`removal-parking-${order?.id}`}>{order?.propertyHasParking ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                    {(order?.packingMaterialsId || order?.storageId) && (
                      <div className="cost">
                        <Label htmlFor={`removal-extras-${order?.id}`}>Extras:</Label>
                        <div className="costDetails">
                          {order?.packingMaterialsId && (
                            <>
                              <Label htmlFor={`removal-packing-${order?.id}`}>Packing Materials:</Label>
                              <p id={`removal-packing-${order?.id}`}>Included</p>
                            </>
                          )}
                          {order?.storageId && (
                            <>
                              <Label htmlFor={`removal-storage-${order?.id}`}>Storage:</Label>
                              <p id={`removal-storage-${order?.id}`}>{order?.storageSize}</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : order?.suitcaseOrder && (
            <div className="customer-print-doc">
              <div className="items-block">
                {orderBoxes.map((orderBox, index) => {
                  return (
                    <OrderBoxesDisplay
                      key={index}
                      id={orderBox.id}
                      suitcaseOrder={order?.suitcaseOrder}
                      barcode={orderBox?.barcode}
                      orderBox={orderBox}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <NoteBlock notes={notes} />
          <StatusBlock statuses={statuses} />
          <PaymentStatusBlock payments={payments} />
          {loggedInUser?.role === Role.Admin && (
            <PaymentBlock
              initialCost={order?.orderType === 'removal' ? order?.totalInitialCost : order?.initialCost}
              monthlyCost={order?.orderType === 'removal' ? order?.monthlyStorageCost : order?.monthlyCost}
              storageRequired={order?.orderType === 'removal' ? !!order?.storageId : order?.storageRequired}
            />
          )}

          <span />
        </div>
        {!order?.suitcaseOrder && order?.orderType !== 'removal' && (
          <div className="print-doc admin-print-doc barcode-doc">
            <div className="top-border">Barcodes</div>
            <div className="items-block">
              <DataTable<OrderBoxesResponse>
                columns={barcodeColumns}
                rows={orderBoxes}
                keyOf={(row) => row.id}
                mobileCard
                emptyMessage="No barcodes for this order."
              />
            </div>
          </div>
        )}
      </section>

      <div className="btn-row">
        {loggedInUser?.role === Role.Admin && order?.orderType !== 'removal' && (
          <SubmitButton
            red={true}
            onClick={() => history.push(`/admin/order/${id}/edit`)}
          >
            Edit
          </SubmitButton>
        )}
        {order?.orderType !== 'removal' && (
          <SubmitButton
            disabled={!order}
            onClick={() => {
              axiosKK
                .get(`/order/${order?.id}/label`, {
                  responseType: 'blob',
                })
                .then((response) => {
                  FileDownload(response.data, `order-${id}-labels.pdf`);
                });
            }}
          >
            Download
          </SubmitButton>
        )}
        {loggedInUser?.role === Role.Admin && order?.orderType !== 'removal' && (
          <SubmitButton onClick={() => setShowONSModal(true)}>
            Create DPD Label
          </SubmitButton>
        )}
      </div>

      {order && showDPDModal && (
        <DPDShippingModal
          isOpen={showDPDModal}
          close={() => setShowONSModal(false)}
          collectionAddress={order.collectionAddress || ''}
          deliveryAddress={order.deliveryAddress || ''}
          orderBoxes={order.orderBoxes}
          orderId={order.id}
          customer={customer}
          orderCollectionDate={order.collectionDate}
        />
      )}
    </AdminPageShell>
  );
}

export default withRouter(AdminOrderComponent);
